import {EAutocompleteOption} from 'webapp-lib/Components';

import {ContactEmailAddressWithInvolvements} from '@lexian/api/contacts-api';
import {
  CreateTemporaryDocumentFromTemplateParams,
  PageInResponse,
  PlaceholderInResponse,
} from '@lexian/api/documents-api';
import {ResponseWithBody} from '@lexian/api/documents-async-backend';
import {DocumentManagementUserSpecificPushNotifications} from '@lexian/api/notifications-async-backend';

export type Notification = ResponseWithBody<
  200,
  DocumentManagementUserSpecificPushNotifications
>;

export enum TemplateWizardStep {
  ChooseTemplate = 'ChooseTemplate',
  Preview = 'Preview',
  Recipient = 'Recipient',
}

export type TemplatePlaceholderWizardStep = TemplateWizardStep | string;

export type LabeledTemplatePlaceholderWizardStep = {
  key: TemplatePlaceholderWizardStep;
  label: string;
};

export type CreateTemporaryDocumentProps = Pick<
  CreateTemporaryDocumentFromTemplateParams,
  | 'datePlaceholders'
  | 'entityPlaceholders'
  | 'stringPlaceholders'
  | 'numberPlaceholders'
>;

export type ChooseTemplateData = {
  casusId?: string;
  templateId?: string;
  templateFileId?: string;
  localeId?: string;
};

export type Recipient = ContactEmailAddressWithInvolvements;

export type AttachmentsType = {
  casusId: string;
  documentSelection: string[];
};

export type PlaceholderPage = Omit<PageInResponse, 'placeholders'> & {
  placeholders: Placeholder[];
};

export type Placeholder = Omit<PlaceholderInResponse, 'placeholders'> & {
  placeholders?: Placeholder[];
  value?: string | EAutocompleteOption[];
  doNotAutoUpdate?: boolean;
  identifier?: string;
};

// Placeholder types that reference an entity (e.g. a user or a contact) and
// therefore carry their value as `DefaultValueInResponse.entityId` rather
// than `DefaultValueInResponse.value` (which holds the display name).
export const ENTITY_PLACEHOLDER_TYPES = [
  'CONTACT',
  'COLLABORATOR',
  'INVOLVED_PARTY',
  'CASUS',
  'CLIENT',
  'USER',
  'OFFICE_LOCATION',
  'SPECIALITY_FIELD',
  'CLIENT_MONEY_BANK_ACCOUNT',
] as const;

export type EntityPlaceholderType = (typeof ENTITY_PLACEHOLDER_TYPES)[number];

export const isEntityPlaceholderType = (
  type: Placeholder['type']
): type is EntityPlaceholderType =>
  (ENTITY_PLACEHOLDER_TYPES as readonly string[]).includes(type);
